import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  SkyBadge,
  SkyCheckbox,
  SkyDialog,
  SkyLoader,
  SkyText,
  useToast,
} from "@skypointcloud/skypoint-ui";
import { baseApi } from "../../store/api/baseApi";
import {
  useGetBulkEmailBatchQuery,
  useProcessBulkEmailMutation,
  useRetryBulkEmailMutation,
  type BulkEmailBatch,
} from "../../store/api/emailApi";
import {
  isFreshFor,
  plural,
  trackBulkEmail,
  untrackBulkEmail,
  useBulkEmailJobs,
  type BulkEmailJob,
} from "./bulkEmailJobs";

/**
 * Follows bulk email sends after the Send Email dialog has closed.
 *
 * Polls each batch, drives it when the server's queue is down, and reports the
 * result as a toast with the failures one click away. Progress while it runs is
 * shown on the Applicants page (BulkEmailProgressStrip), from the status this
 * polls. Mounted once, in AppLayout, so a send is still followed, and its
 * result still shown, while the recruiter moves around the app.
 */

/** Polls fast while a typical batch is still running, then eases off the rate limit. */
const FAST_POLL_MS = 3000;
const SLOW_POLL_MS = 8000;
const FAST_POLL_FOR_MS = 30000;

function BulkEmailJobWatcher({
  job,
  onShowDetails,
}: {
  job: BulkEmailJob;
  onShowDetails: (job: BulkEmailJob) => void;
}) {
  // Read through a ref so the effects below never re-run just because the
  // toast API object changed identity.
  const toastApi = useToast();
  const toastRef = useRef(toastApi);
  toastRef.current = toastApi;
  const dispatch = useDispatch();
  const [processBulkEmail] = useProcessBulkEmailMutation();
  const [pollMs, setPollMs] = useState(FAST_POLL_MS);
  const drove = useRef(false);
  const finished = useRef(false);

  const { data, error, fulfilledTimeStamp } = useGetBulkEmailBatchQuery(
    { instanceId: job.instanceId, batchId: job.batchId },
    { pollingInterval: pollMs, refetchOnMountOrArgChange: true, skipPollingIfUnfocused: false },
  );
  const batch = isFreshFor(job, fulfilledTimeStamp) ? data : undefined;

  useEffect(() => {
    const timer = window.setTimeout(() => setPollMs(SLOW_POLL_MS), FAST_POLL_FOR_MS);
    return () => window.clearTimeout(timer);
  }, []);

  // The queue is down: this tab asks the server to send, inside that request.
  // Safe to repeat (after a refresh, say): the server returns at once if the
  // batch is already being sent, and its sweep finishes it if this tab closes.
  useEffect(() => {
    if (job.queued || drove.current) return;
    drove.current = true;
    processBulkEmail({ instanceId: job.instanceId, batchId: job.batchId })
      .unwrap()
      .catch(() => {
        // Polling still reports progress; the server's sweep picks the batch up.
      });
  }, [job.queued, job.instanceId, job.batchId, processBulkEmail]);

  useEffect(() => {
    const toast = toastRef.current;
    if (finished.current || batch?.status !== "completed") return;
    finished.current = true;

    // One refresh for the whole batch: last-contacted and "reached" change.
    dispatch(baseApi.util.invalidateTags(["Applicants", "EmailHistory"]));

    const { sent, failed, skipped } = batch.counts;
    const notSent = failed + skipped;
    if (notSent === 0) {
      toast.success(`Sent to ${plural(sent, "applicant")}.`, { title: "Emails sent" });
    } else {
      const showDetails = {
        label: "View details",
        onClick: (toastId: string) => {
          toast.dismiss(toastId);
          onShowDetails(job);
        },
      };
      if (sent === 0) {
        toast.error(`None of the ${plural(batch.total, "email")} went out.`, {
          title: "Emails weren't sent",
          duration: null,
          action: showDetails,
        });
      } else {
        toast.warning(`Sent ${sent} of ${batch.total}. ${notSent} not sent.`, {
          title: "Some emails weren't sent",
          duration: null,
          action: showDetails,
        });
      }
    }
    untrackBulkEmail(job.batchId);
  }, [batch, dispatch, job, onShowDetails]);

  // Gone (another account, or cleaned up): stop following it.
  const notFound = (error as { status?: unknown } | undefined)?.status === 404;
  useEffect(() => {
    const toast = toastRef.current;
    if (!notFound || finished.current) return;
    finished.current = true;
    toast.error("We couldn't find that email send. Check the applicants' Communication History.");
    untrackBulkEmail(job.batchId);
  }, [notFound, job.batchId]);

  return null;
}

function problemBadge(problem: BulkEmailBatch["problems"][number]) {
  if (problem.status === "skipped") return { color: "informative" as const, label: "Skipped" };
  if (problem.outcomeUnknown) return { color: "warning" as const, label: "Unconfirmed" };
  return { color: "danger" as const, label: "Failed" };
}

export function BulkEmailDetailsDialog({
  batchId,
  instanceId,
  onClose,
}: {
  batchId: string;
  instanceId: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const { data, isLoading, isError } = useGetBulkEmailBatchQuery(
    { instanceId, batchId },
    { refetchOnMountOrArgChange: true },
  );
  const [retryBulkEmail, { isLoading: retrying }] = useRetryBulkEmailMutation();
  const [includeUnknown, setIncludeUnknown] = useState(false);
  const retried = useRef(false);

  const problems = data?.problems ?? [];
  const certainFailures = problems.filter((p) => p.status === "failed" && !p.outcomeUnknown);
  const unconfirmed = problems.filter((p) => p.status === "failed" && p.outcomeUnknown);
  const retryCount = certainFailures.length + (includeUnknown ? unconfirmed.length : 0);
  const canRetry = data?.status === "completed" && retryCount > 0;

  const handleRetry = async () => {
    try {
      const result = await retryBulkEmail({ instanceId, batchId, includeUnknown }).unwrap();
      if (result.requeued === 0) {
        toast.info("Nothing left to retry.");
      } else {
        trackBulkEmail({
          batchId,
          instanceId,
          total: data?.total ?? result.requeued,
          queued: result.queued,
        });
      }
      retried.current = true;
      onClose();
    } catch {
      toast.error("Couldn't retry. Please try again.");
    }
  };
  return (
    <SkyDialog
      open
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
      // A failed retry keeps the dialog, and the choice made in it.
      onBeforeClose={(reason) => reason !== "primaryAction" || retried.current}
      title="Bulk email results"
      size="md"
      primaryAction={
        canRetry
          ? {
              label: retrying ? "Retrying..." : `Retry ${plural(retryCount, "email")}`,
              onClick: handleRetry,
              disabled: retrying,
            }
          : undefined
      }
      secondaryAction={{ label: "Close", onClick: onClose }}
    >
      {isLoading ? (
        <div className="flex justify-center py-6">
          <SkyLoader
            size="small"
            label="Loading results..."
          />
        </div>
      ) : isError || !data ? (
        <SkyText className="text-gray-600 dark:text-muted-foreground">
          These results are no longer available.
        </SkyText>
      ) : (
        <div className="flex flex-col gap-4">
          <SkyText>
            Sent {data.counts.sent} of {data.total}.
            {problems.length > 0 && ` ${problems.length} not sent:`}
          </SkyText>
          {problems.length > 0 && (
            <ul className="flex flex-col divide-y divide-gray-200 dark:divide-border border border-gray-200 dark:border-border rounded-md max-h-80 overflow-y-auto m-0 p-0 list-none">
              {problems.map((problem) => {
                const badge = problemBadge(problem);
                return (
                  <li
                    key={problem.applicantId}
                    className="flex items-start justify-between gap-3 px-3 py-2"
                  >
                    <div className="flex flex-col min-w-0">
                      <SkyText
                        size={300}
                        weight="semibold"
                        block
                        className="truncate"
                      >
                        {problem.fullName || "Unknown applicant"}
                      </SkyText>
                      {problem.email && (
                        <SkyText
                          size={200}
                          block
                          className="text-gray-500 dark:text-muted-foreground truncate"
                        >
                          {problem.email}
                        </SkyText>
                      )}
                      {problem.error && (
                        <SkyText
                          size={200}
                          block
                          className="text-gray-600 dark:text-muted-foreground"
                        >
                          {problem.error}
                        </SkyText>
                      )}
                    </div>
                    <SkyBadge
                      color={badge.color}
                      appearance="tint"
                      size="small"
                    >
                      {badge.label}
                    </SkyBadge>
                  </li>
                );
              })}
            </ul>
          )}
          {unconfirmed.length > 0 && data.status === "completed" && (
            <SkyCheckbox
              label={`Also resend ${plural(unconfirmed.length, "unconfirmed email")}`}
              description="Sending was interrupted, so these candidates may already have the email. Check their Communication History first."
              value={includeUnknown}
              onChange={(state) => setIncludeUnknown(state === true)}
            />
          )}
        </div>
      )}
    </SkyDialog>
  );
}

export function BulkEmailTracker() {
  const jobs = useBulkEmailJobs();
  const [details, setDetails] = useState<{ batchId: string; instanceId: string } | null>(null);
  const showDetails = useCallback(
    (job: BulkEmailJob) => setDetails({ batchId: job.batchId, instanceId: job.instanceId }),
    [],
  );

  return (
    <>
      {jobs.map((job) => (
        <BulkEmailJobWatcher
          key={`${job.batchId}-${job.startedAt}`}
          job={job}
          onShowDetails={showDetails}
        />
      ))}
      {details && (
        <BulkEmailDetailsDialog
          batchId={details.batchId}
          instanceId={details.instanceId}
          onClose={() => setDetails(null)}
        />
      )}
    </>
  );
}
